import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import doorImage from '@/assets/images/door.webp';
import { Button, Flex, HeaderItalic, Link, LoadingState, Text } from '@/components/common';
import { useAuth } from '@/hooks';
import { subscriptionService } from '@/services/subscriptionService';
import type { ProductPrice } from '@/types';
import { ProductSlider } from './ProductSlider';

function getPurchaseErrorMessage(err: unknown, t: TFunction<'subscription'>): string {
  const code = (err as { code?: string }).code;
  if (code === 'functions/failed-precondition') return t('paywall.alreadySubscribed');
  if (err instanceof Error) return err.message;
  return t('purchase.error');
}

export function Paywall() {
  const { t } = useTranslation('subscription');
  const { t: tCommon } = useTranslation('common');
  const { user } = useAuth();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const { data: products, isLoading } = useQuery({
    queryKey: ['stripePrices', user?.uid],
    queryFn: async () => subscriptionService.getStripePrices(),
    enabled: !!user,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const handlePurchase = async (priceId: string) => {
    if (isCheckingOut) return;
    setIsCheckingOut(true);
    try {
      const { url } = await subscriptionService.createStripeCheckout(
        priceId,
        window.location.origin
      );
      window.location.assign(url);
    } catch (err) {
      toast.error(getPurchaseErrorMessage(err, t));
      setIsCheckingOut(false);
    }
  };

  if (isLoading) {
    return <LoadingState />;
  }

  const hasProducts = products && products.length > 0;
  const currentIndex = hasProducts ? (selectedIndex ?? Math.floor(products.length / 2)) : 0;
  const product: ProductPrice | undefined = hasProducts ? products[currentIndex] : undefined;
  const selectedPrice = product
    ? (product.prices.month ?? Object.values(product.prices)[0])
    : undefined;

  return (
    <Flex direction="column">
      <img
        alt=""
        decoding="async"
        loading="eager"
        src={doorImage}
        style={{ height: '10rem', objectFit: 'cover', width: '100%' }}
      />

      <Flex direction="column" px="4" mb="3">
        <Flex align="center" direction="column" gap="4" pt="6">
          <HeaderItalic align="center" as="h1" color="white" size="5">
            {t('paywall.title')}
          </HeaderItalic>
          <Text align="center" size="1" color="gray">
            {t('paywall.description')}
          </Text>
        </Flex>

        {hasProducts && products ? (
          <ProductSlider
            products={products}
            selectedIndex={currentIndex}
            onSelect={setSelectedIndex}
          />
        ) : (
          <Flex direction="column" gap="4">
            <Text align="center" color="red" size="2">
              {t('noOfferings')}
            </Text>
          </Flex>
        )}

        <Flex direction="column" gap="3" mt="4">
          <Text align="center" size="1" color="gray">
            {t('paywall.promoNote')}
          </Text>
          <Button
            disabled={!hasProducts}
            isLoading={isCheckingOut}
            fullWidth
            onClick={() => {
              if (selectedPrice) void handlePurchase(selectedPrice.id);
            }}
            size="lg"
            color="gray"
            radius="full"
            highContrast
          >
            {t('paywall.join')}
          </Button>
          <Flex gap="2" justify="center" mt="1">
            <Link href="/terms" size="1" color="gray">
              {tCommon('legal.terms')}
            </Link>
            <Text size="1" color="gray">
              /
            </Text>
            <Link href="/privacy" size="1" color="gray">
              {tCommon('legal.privacy')}
            </Link>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
